import dayjs from "dayjs";
import { Invoice, InvoicePaymentRequest } from "../../../models/Invoice/Invoice";
import { InvoiceRequest } from "../../../models/Invoice/InvoiceRequest";
import { InvoiceFilters } from "../../../models/Invoice/InvoiceFilters";
import { AdvancePayment } from "../../../models/Order/AdvancePayment";
import { OrderBasicDetails } from "../../../models/Order/OrderDetails";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  deleteAPI,
  getAPI,
  downloadPDF,
  postAPI,
  downloadFile,
  putAPI,
} from "../../../api/Networking";
import { GetPurchaseOrderInvoicesParams } from "../../../models/Invoice/PurchaseOrderInvoiceFilters";
import { PurchaseOrderInvoice } from "../../../models/Invoice/PurchaseOrderInvoice";
import { PurchaseOrderInvoiceUploadRequest } from "../../../models/Invoice/PurchaseOrderInvoiceUploadRequest";
import { ActivityLog } from "../../../models/Activity/ActivityLog";

export default class InvoiceAPI {
  createInvoice = async (invoiceRequest: InvoiceRequest) => {
    try {
      const body = invoiceRequest;
      const response = await postAPI(`/v5/invoice`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderDetailsById = async (orderId: number): Promise<OrderBasicDetails> => {
    try {
      const response = await getAPI(`/v5/orders/order-id/${orderId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getInvoiceDetailsById = async (invoiceId: number): Promise<Invoice> => {
    try {
      const response = await getAPI(`/v5/invoice/${invoiceId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addPaymentInfo = async (paymentInfo: InvoicePaymentRequest) => {
    try {
      const response = await postAPI(`/v5/invoice/payment`, paymentInfo);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deletePayment = async (deletePayment: InvoicePaymentRequest) => {
    try {
      const response = await deleteAPI(`/v5/invoice/payment`, deletePayment);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadInvoice = async (id: number) => {
    try {
      const response = await downloadPDF(`/v5/invoice/${id}/download`);
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  downloadOldInvoice = async (id: number) => {
    try {
      const response = await downloadPDF(`/v5/invoice/${id}/download/old`);
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  downloadInvoiceByOrderId = async (id: number) => {
    try {
      const response = await downloadFile(`/v5/orders/${id}/invoices/download`);
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  downloadOldInvoiceByOrderId = async (id: number) => {
    try {
      const response = await downloadFile(
        `/v5/orders/${id}/invoices/download/old`
      );
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  deleteInvoice = async (
    order_id: number,
    id: number,
    productId?: number,
    serviceId?: number,
    customName?: string,
    reason?: string
  ) => {
    try {
      if (productId) {
        const response = await deleteAPI(`/v5/invoice`, {
          id,
          order_id,
          item_id: productId,
        });
        return response.result;
      } else if (serviceId) {
        const response = await deleteAPI(`/v5/invoice`, {
          id,
          order_id,
          service_id: serviceId,
          custom_name: customName,
        });
        return response.result;
      } else {
        const response = await deleteAPI(`/v5/invoice`, {
          id,
          order_id,
          delete_reason: reason,
        });
        return response.result;
      }
    } catch (error) {
      throw error;
    }
  };

  reactivateInvoice = async (
    order_id: number,
    id: number,
    reactivate_reason?: string
  ): Promise<Invoice> => {
    try {
      const body: {
        id: number;
        order_id: number;
        reactivate_reason?: string;
      } = { id, order_id };
      if (reactivate_reason && reactivate_reason.trim() !== "") {
        body.reactivate_reason = reactivate_reason.trim();
      }
      const response = await putAPI(`/v5/invoice/reactivate`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  syncInvoiceWithZoho = async (invoiceId: number): Promise<Invoice> => {
    try {
      const response = await getAPI(`/v5/invoice/${invoiceId}/sync-with-zoho`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getAdvancePayments = async (orderId: number): Promise<AdvancePayment[]> => {
    try {
      const response = await getAPI(`/v5/invoice/advance-payment/${orderId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateAdvancePayment = async (
    value: InvoicePaymentRequest
  ): Promise<AdvancePayment[]> => {
    try {
      const response = await postAPI(`/v5/invoice/advance-payment`, value);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  editAdvancePayment = async (
    id: number,
    value: InvoicePaymentRequest
  ): Promise<AdvancePayment[]> => {
    try {
      const response = await putAPI(
        `/v5/invoice/advance-payment/${id}`,
        value
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteAdvancePayment = async (
    orderId: number,
    id: number
  ): Promise<AdvancePayment[]> => {
    try {
      const response = await deleteAPI(
        `/v5/invoice/advance-payment/${orderId}/${id}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateInvoice = async (invoiceRequest: InvoiceRequest) => {
    try {
      const body = invoiceRequest;
      const response = await putAPI(`/v5/invoice`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadInvoicesInBackground = async (
    start_date: string,
    end_date: string
  ) => {
    try {
      const param_start_date = dayjs(start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const param_end_date = dayjs(end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      const queryParams = new URLSearchParams();

      if (param_start_date) queryParams.append("start_date", param_start_date);

      if (param_end_date) queryParams.append("end_date", param_end_date);
      const response = await getAPI(
        `/v5/invoice/download/all/background?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getInvoices = async (
    filters: InvoiceFilters
  ): Promise<PaginatedAPIResponse<Invoice>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit) queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text.toString());

      if (filters.sort_by)
        queryParams.append("sort_by", filters.sort_by.toString());

      if (filters.sort_type)
        queryParams.append("sort_type", filters.sort_type.toString());

      const response = await getAPI(`/v5/invoice?${queryParams.toString()}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseOrderInvoices = async (
    params: GetPurchaseOrderInvoicesParams
  ): Promise<PaginatedAPIResponse<PurchaseOrderInvoice>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType)
        queryParams.append("sort_type", params.sortType.toString());
      if (params.search_text)
        queryParams.append("search_text", params.search_text);
      if (params.approval_status)
        queryParams.append("approval_status", params.approval_status);
      if (params.invoice_payment_status)
        queryParams.append(
          "invoice_payment_status",
          params.invoice_payment_status
        );
      if (params.payment_cycle_date)
        queryParams.append("payment_cycle_date", params.payment_cycle_date);

      const response = await getAPI(
        `/v5/invoice/po-invoices?${queryParams.toString()}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approvePurchaseOrderInvoiceRequest = async (invoiceId: number) => {
    try {
      const response = await putAPI(
        `/v5/invoice/purchase-order-invoices/approve/${invoiceId}`,
        {
          approved: true,
        }
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  rejectPurchaseOrderInvoiceRequest = async (
    invoiceId: number,
    rejectionReason?: string
  ) => {
    try {
      const response = await putAPI(
        `/v5/invoice/purchase-order-invoices/reject/${invoiceId}`,
        {
          rejected: true,
          rejection_reason: rejectionReason || "",
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  markPurchaseOrderInvoicePaymentStatus = async (
    invoiceId: number,
    paymentStatus: string
  ) => {
    try {
      const response = await putAPI(
        `/v5/invoice/purchase-order-invoices/${invoiceId}/payment-status`,
        {
          invoice_payment_status: paymentStatus,
        }
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  uploadInvoice = async (
    request: PurchaseOrderInvoiceUploadRequest
  ) => {
    try {
      const body: any = {
        po_id: request.po_id,
        vendor_id: request.vendor_id,
        invoice_url: request.invoice_url,
        invoice_number: request.invoice_number,
        invoice_amount: request.invoice_amount,
        invoice_date: request.invoice_date,
      };

      if (request.invoice_items !== undefined) {
        body.products = request.invoice_items;
      }

      if (request.other_charges !== undefined) {
        body.other_charges = request.other_charges;
      }

      const response = await postAPI(`/v5/invoice/purchase-order-invoices`, body);
      return response;
    } catch (error) {
      throw error;
    }
  };

  recalculateInvoice = async (invoiceId: number) => {
    try {
      const response = await putAPI(
        `/v5/invoice/${invoiceId}/recalculate`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getInvoiceActivityLogs = async (invoiceId: number): Promise<ActivityLog[]> => {
    try {
      const response = await getAPI(`/v5/invoice/${invoiceId}/activity-logs`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
